import type { ButtonHTMLAttributes, ReactNode } from 'react';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks interaction. */
  isLoading?: boolean;
  /** Accessible text announced while loading. */
  loadingLabel?: string;
  leadingIcon?: ReactNode;
  fullWidth?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  loadingLabel = 'Loading',
  leadingIcon,
  fullWidth = false,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    'sc-button',
    `sc-button--${variant}`,
    `sc-button--${size}`,
    fullWidth ? 'sc-button--block' : '',
    isLoading ? 'sc-button--loading' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      {...rest}
      type={type}
      className={classes}
      disabled={disabled ?? isLoading}
      aria-busy={isLoading || undefined}
    >
      {isLoading && <span className="sc-button__spinner" aria-hidden="true" />}
      {!isLoading && leadingIcon && (
        <span className="sc-button__icon" aria-hidden="true">
          {leadingIcon}
        </span>
      )}
      <span>{children}</span>
      {isLoading && <span className="sc-visually-hidden">{loadingLabel}</span>}
    </button>
  );
}
